import {
	SecondaryTokenFetchFailure,
	AMLErrorCode,
	AMErrorCode,
	authErrorString,
	PrimaryTokenFetchFailure,
	GatewaySessionError,
} from '../AuthErrors';
import { HeaderParser } from './parser/HeaderParser';
import {
	RequestTokenResponse,
	RequestTokenResponseParser,
} from './parser/RequestTokenResponseParser';
import DSAuth from './DSAuth';
import { AuthChallenge } from './data/AuthChallenge';
import TokenManager from '../TokenManager';
import {
	SecondaryTokenResponse,
	HTTPFetchResponse,
	ResponseToNative,
	RequestTokenFailureResponse,
	StoreSecondaryTokenFunctionParams,
	AuthResponseWithToken,
	SecondaryTokenTableEntry,
	AuthenticationMode,
	SecondaryTokenResponseToNative,
	PrimaryTokenTableEntry,
} from './data/AuthTypes';
import {
	statusConstants,
	integerConstants,
	stringConstants,
	primaryTokenStatusConstants,
} from '../AuthConstants';
import { logConstants } from '../../utils/Constants';
import AuthUtils from '../AuthUtils';
import { config } from '../AuthConfig';
import { Logger } from '../Logger';
import { RequestParams } from './data/AuthTypes';
import { CookieManager } from '../CookieManager';
import { PrimaryTokenServiceWrapper } from '../PrimaryTokenServiceWrapper';
import AuthMan from '../AuthMan';
import Utils from '../../utils/Utils';
import { HttpRequestError } from '../../utils/http/HttpErrors';
import { SentryLogger } from '../../sentryLogging/SentryLogger';
import * as Sentry from '@sentry/browser';
import ctxsNative from '../ctxsNative';
import { HttpStatusCodes } from '../../utils/http/HttpConstants';
import { AuthBridgeFactory } from '../../AccountConfig/bridge/auth/AuthBridgeFactory';

const authBridge = AuthBridgeFactory.getBridge();

export default abstract class SecondaryTokenServiceBase {
	protected tokenManager: TokenManager;
	private dsAuth: DSAuth;

	constructor() {
		this.tokenManager = TokenManager.getInstance();
		this.dsAuth = DSAuth.getInstance();
	}

	/// <summary>
	/// check if cached token for url is present in db and make request
	/// check response status code and take appropriate action
	/// </summary>
	/// <param name="requestParams">RequestParams object (refer it's type definition for details)</param>
	/// <returns>ResponseToNative object</returns>
	async getAuthResponseForURL(requestParams: RequestParams): Promise<ResponseToNative> {
		try {
			if (
				typeof requestParams.referenceUri !== 'undefined' &&
				requestParams.referenceUri !== null
			) {
				const storeConfig = AuthMan.getInstance().getStoreConfiguration();
				if (storeConfig && !storeConfig[requestParams.referenceUri]) {
					Logger.getInstance().write(
						logConstants.ERROR,
						requestParams.id +
							':Reference uri not present in store configuration sent by Native'
					);
				}

				const urlToPrint = Utils.getInstance().removeQueryStringAndHashFromUrl(
					requestParams.url
				);
				Logger.getInstance().write(
					logConstants.INFO,
					requestParams.id + ':Starting Auth for url: ' + urlToPrint
				);
				const { authResponse, tokenObj } =
					await this.getAuthResponseUsingCachedTokenForURL(requestParams);

				if (authResponse.statusCode != statusConstants['401']) {
					return AuthUtils.getInstance().constructAMLResponse({
						id: requestParams.id,
						URLResponse: authResponse,
						token: tokenObj && requestParams.returnSecondaryToken ? tokenObj.token : null,
					});
				}

				return await this.handle401Response(requestParams, authResponse, tokenObj);
			} else {
				throw new Error(stringConstants.ERROR_MISSING_REFERENCE_URI);
			}
		} catch (e) {
			return await this.handleErrorsWhileFetchingSecondaryToken(requestParams, e);
		}
	}
	private async handleErrorsWhileFetchingSecondaryToken(
		requestParams: RequestParams,
		e: unknown
	): Promise<ResponseToNative> {
		const errorMsg = (e as Error).message;
		Logger.getInstance().write(
			logConstants.INFO,
			requestParams.id + ':DSAUTH_SECONDARY_TOKEN_FETCH_FAIL' + errorMsg
		);
		if (e instanceof GatewaySessionError) {
			return await this.handleGatewaySessionExpiredError(requestParams, e);
		}
		if (e instanceof PrimaryTokenFetchFailure) {
			return await this.handlePrimaryTokenFetchFailureError(requestParams, e);
		}
		if (
			errorMsg === stringConstants.INVALID_PRIMARY_TOKEN_SERVICE_URL ||
			errorMsg == authErrorString.DSAUTH_TOKEN_REQUEST_RESPONSE_AUTHDOMAIN_MISMATCH
		) {
			return this.handlePossibleAuthDomainChangeError(requestParams, errorMsg);
		}
		if (errorMsg == stringConstants.ERROR_MISSING_REFERENCE_URI) {
			return this.handleMissingReferenceUriError(requestParams, e);
		}
		if (e instanceof HttpRequestError) {
			return this.handleHttpRequestError(requestParams, e);
		}
		throw new SecondaryTokenFetchFailure(e as Error);
	}

	private async handlePrimaryTokenFetchFailureError(
		requestParams: RequestParams,
		e: PrimaryTokenFetchFailure
	): Promise<ResponseToNative> {
		await CookieManager.getInstance().deleteSessionCookiesForReferenceUri(
			requestParams.referenceUri
		);
		if (
			e.innerError &&
			e.innerError.message == primaryTokenStatusConstants.ERROR_CANCEL_LOGIN
		) {
			Logger.getInstance().write(
				logConstants.ERROR,
				requestParams.id + ':' + authErrorString.DSAUTH_LOGIN_CANCELLED_BY_USER
			);
			return AuthUtils.getInstance().constructAMLResponse({
				id: requestParams.id,
				errorCode: AMErrorCode.AM_AUTH_RESULT_CANCELLED_BY_USER,
				errorMessage: authErrorString.DSAUTH_LOGIN_CANCELLED_BY_USER,
			});
		} else if (
			e.innerError &&
			e.innerError.message == primaryTokenStatusConstants.ERROR_SILENT_LOGIN_FAILURE
		) {
			Logger.getInstance().write(
				logConstants.ERROR,
				requestParams.id +
					':' +
					authErrorString.DSAUTH_SILENTLOGIN_THROUGH_OIDCREFRESH_FAILED
			);
			return AuthUtils.getInstance().constructAMLResponse({
				id: requestParams.id,
				errorCode: AMLErrorCode.AML_SILENT_LOGIN_FAILURE,
				errorMessage: authErrorString.DSAUTH_SILENTLOGIN_THROUGH_OIDCREFRESH_FAILED,
			});
		} else if (
			e.innerError &&
			e.innerError.message == primaryTokenStatusConstants.ERROR_AUTHDOMAIN_CHANGED
		) {
			Logger.getInstance().write(
				logConstants.ERROR,
				requestParams.id +
					':' +
					authErrorString.DSAUTH_TOKEN_REQUEST_RESPONSE_AUTHDOMAIN_MISMATCH
			);
			const breadcrumb = {
				category: 'SecondaryTokenService',
				message: e.message,
				level: Sentry.Severity.Error,
			};
			SentryLogger.getInstance().addBreadCrumb(breadcrumb);
			return AuthUtils.getInstance().constructAMLResponse({
				id: requestParams.id,
				errorCode: AMLErrorCode.AML_POSSIBLE_AUTH_DOMAIN_CHANGE,
				errorMessage: authErrorString.DSAUTH_TOKEN_REQUEST_RESPONSE_STATUS_400_OR_404,
			});
		}
		throw e;
	}

	private async handleGatewaySessionExpiredError(
		requestParams: RequestParams,
		e: GatewaySessionError
	): Promise<ResponseToNative> {
		return AuthUtils.getInstance().constructAMLResponse({
			id: requestParams.id,
			URLResponse: {
				statusCode: HttpStatusCodes.HTTP_STATUS_DENIED,
				body: '',
				headers: {},
			},
			errorCode: AMLErrorCode.AML_START_URL_REQUEST_EXCEPTION,
			errorMessage: e.message,
		});
	}

	private handleMissingReferenceUriError(
		requestParams: RequestParams,
		e: unknown
	): ResponseToNative {
		Logger.getInstance().write(
			logConstants.ERROR,
			requestParams.id + ':' + authErrorString.DSAUTH_REFERENCE_URI_MISSING
		);
		SentryLogger.getInstance().logError(e);
		return AuthUtils.getInstance().constructAMLResponse({
			id: requestParams.id,
			errorCode: AMLErrorCode.AML_REQUEST_MISSING_REFERENCE_URI,
			errorMessage: authErrorString.DSAUTH_REFERENCE_URI_MISSING,
		});
	}

	private handleHttpRequestError(
		requestParams: RequestParams,
		e: HttpRequestError
	): ResponseToNative {
		return AuthUtils.getInstance().constructAMLResponse({
			id: requestParams.id,
			errorCode: e.errorCode,
			errorMessage: e.message,
		});
	}

	private handlePossibleAuthDomainChangeError(
		requestParams: RequestParams,
		errorMessage: string
	): ResponseToNative {
		Logger.getInstance().write(logConstants.ERROR, requestParams.id + ':' + errorMessage);
		const breadcrumb = {
			category: 'SecondaryTokenService',
			message: errorMessage,
			level: Sentry.Severity.Error,
		};
		SentryLogger.getInstance().addBreadCrumb(breadcrumb);
		return AuthUtils.getInstance().constructAMLResponse({
			id: requestParams.id,
			errorCode: AMLErrorCode.AML_POSSIBLE_AUTH_DOMAIN_CHANGE,
			errorMessage: authErrorString.DSAUTH_TOKEN_REQUEST_RESPONSE_AUTHDOMAIN_MISMATCH,
		});
	}
	/// <summary>
	/// check if cached token for url is present in db and make request
	/// </summary>
	/// <param name="requestParams">RequestParams object (refer it's type definition for details)</param>
	/// <returns>AuthResponseWithToken object which contains the http response and token used in the request</returns>
	private async getAuthResponseUsingCachedTokenForURL(
		requestParams: RequestParams
	): Promise<AuthResponseWithToken> {
		const tokenObj = await this.tokenManager.getCachedSecondaryTokenForURL(
			requestParams.url,
			requestParams.authDomain,
			requestParams.referenceUri,
			requestParams.id
		);
		let token = null;
		if (tokenObj) {
			token = tokenObj.token;
		}
		const authResponse = await this.makeAuthorizedRequest(requestParams, token);
		return { authResponse, tokenObj };
	}
	/**
	 * Returns cached secondary token if present into the cache otherwise it tries to get the the token from the server
	 *
	 * @param {RequestParams} requestParams
	 * @return {*}  {Promise<string>}
	 * @memberof SecondaryTokenService
	 */
	public async getSecondaryTokenForUrlFromCacheOrNetwork(
		requestParams: RequestParams
	): Promise<SecondaryTokenResponseToNative> {
		let authChallenge: AuthChallenge;
		try {
			if (
				typeof requestParams.referenceUri !== 'undefined' &&
				requestParams.referenceUri !== null
			) {
				const storeConfig = AuthMan.getInstance().getStoreConfiguration();
				if (storeConfig && !storeConfig[requestParams.referenceUri]) {
					Logger.getInstance().write(
						logConstants.ERROR,
						requestParams.id +
							':Reference uri not present in store configuration sent by Native'
					);
				}
				const tokenObj = await this.tokenManager.getCachedSecondaryTokenForURL(
					requestParams.url,
					requestParams.authDomain,
					requestParams.referenceUri,
					requestParams.id,
					false
				);
				if (tokenObj) {
					return AuthUtils.getInstance().constructAMLSecondaryTokenResponseToNative({
						id: requestParams.id,
						token: tokenObj.token,
					});
				}
				const authResponse = await this.makeAuthorizedRequest(requestParams, null);
				if (authResponse.statusCode === statusConstants['401']) {
					try {
						//Parsing 401 challenge
						authChallenge = HeaderParser.getInstance().parseAuthHeader(
							authResponse.headers
						);
					} catch (e) {
						const response =
							AuthUtils.getInstance().constructAMLSecondaryTokenResponseToNative({
								id: requestParams.id,
								errorCode: AMLErrorCode.AML_SECONDARY_TOKEN_FETCH_FAIL,
								errorMessage: e.message,
							});
						return response;
					}
					const transactionId = AuthUtils.getInstance().getCitrixTransactionIdFromHeaders(
						requestParams.headers
					);
					await AuthUtils.getInstance().takeActionForAuthChallenge(
						authChallenge,
						requestParams.referenceUri,
						requestParams.id,
						transactionId,
						false,
						requestParams.url
					);
					const secondaryTokenForRealm =
						await this.tokenManager.getCachedSecondaryTokenForRealm(
							authChallenge.getRealm(),
							requestParams.url,
							requestParams.id,
							requestParams.referenceUri,
							false
						);
					if (secondaryTokenForRealm) {
						return AuthUtils.getInstance().constructAMLSecondaryTokenResponseToNative({
							id: requestParams.id,
							token: secondaryTokenForRealm.token,
						});
					}
					const result = await this.getSecondaryTokenForURL(requestParams, authChallenge);
					if (result.status == integerConstants.TOKEN_RECEIVED) {
						const encryptedToken = await ctxsNative.Encrypt(
							requestParams.referenceUri,
							(result.details as RequestTokenResponse).token
						);
						return AuthUtils.getInstance().constructAMLSecondaryTokenResponseToNative({
							id: requestParams.id,
							token: encryptedToken,
						});
					} else {
						Logger.getInstance().write(
							logConstants.INFO,
							requestParams.id + ': Secondary token fetch incomplete'
						);
						const resultDetails = result.details as RequestTokenFailureResponse;
						return AuthUtils.getInstance().constructAMLSecondaryTokenResponseToNative({
							id: requestParams.id,
							errorCode: resultDetails.errorCode,
							errorMessage: resultDetails.errorMessage,
						});
					}
				} else {
					const response =
						AuthUtils.getInstance().constructAMLSecondaryTokenResponseToNative({
							id: requestParams.id,
							errorCode: AMLErrorCode.AML_SECONDARY_TOKEN_FETCH_FAIL,
							errorMessage: `${authErrorString.STATUS_CODE_OTHER_THAN_401}, code: ${authResponse.statusCode}`,
						});
					return response;
				}
			} else {
				throw new Error(stringConstants.ERROR_MISSING_REFERENCE_URI);
			}
		} catch (e) {
			const response = await this.handleErrorsWhileFetchingSecondaryToken(
				requestParams,
				e
			);
			const responseForSecondaryToken = { ...response };
			delete responseForSecondaryToken.URLResponse;
			return responseForSecondaryToken;
		}
	}
	/// <summary>
	/// 1. Check if token is passed and add it to Authorization header
	/// 2. Make the request
	/// </summary>
	/// <param name="requestParams">RequestParams object (refer it's type definition for details)</param>
	/// <param name="token">decrypted token string</param>
	/// <returns>http response</returns>
	private async makeAuthorizedRequest(
		requestParams: RequestParams,
		token: string
	): Promise<HTTPFetchResponse> {
		let hdrs = JSON.parse(JSON.stringify(requestParams.headers));
		if (token) {
			hdrs = AuthUtils.getInstance().addTokenToRequestHeader(
				requestParams.headers,
				token,
				AuthenticationMode.DSAuth
			);
		}
		return await authBridge.authFetch(
			requestParams.method,
			requestParams.url,
			hdrs,
			requestParams.body,
			requestParams.referenceUri,
			requestParams.allowCredUi,
			requestParams.id
		);
	}

	/// <summary>
	/// 1. Deletes previously used token from db as it gave a 401
	/// 2. Parses 401 challenge to check if token for realm is found in SecondaryTokenTable
	/// 		If realm token is found, make request with realm token
	/// 		return if response is not 401
	/// 3. If response is 401, delete realm token from db, trigger the flow to fetching secondary token from server
	/// 4. if: token is successfully fetched from server
	/// 		Make authorized request and return ResponseToNative
	///		 else: return error in ResponseToNative
	/// </summary>
	/// <param name="requestParams">RequestParams object (refer it's type definition for details)</param>
	/// <param name="auth401Response">http response from previous request which has status code 401</param>
	/// <param name="previouslyUsedToken">token object used which making previous request</param>
	/// <returns>ResponseToNative object</returns>
	private async handle401Response(
		requestParams: RequestParams,
		auth401Response: HTTPFetchResponse,
		previouslyUsedToken: SecondaryTokenTableEntry
	): Promise<ResponseToNative> {
		let authChallenge, response;
		//Deleting prevously used token from db as it gave 401
		if (previouslyUsedToken) {
			await TokenManager.getInstance().deleteSecondaryTokensForRealm(
				previouslyUsedToken.tokenForService,
				requestParams.url,
				requestParams.referenceUri
			);
		}
		try {
			//Parsing 401 challenge
			authChallenge = HeaderParser.getInstance().parseAuthHeader(auth401Response.headers);
		} catch (e) {
			response = AuthUtils.getInstance().constructAMLResponse({
				id: requestParams.id,
				URLResponse: auth401Response,
				errorCode: AMLErrorCode.AML_SECONDARY_TOKEN_FETCH_FAIL,
				errorMessage: e.message,
			});
			return response;
		}
		const transactionId = AuthUtils.getInstance().getCitrixTransactionIdFromHeaders(
			requestParams.headers
		);

		await AuthUtils.getInstance().takeActionForAuthChallenge(
			authChallenge,
			requestParams.referenceUri,
			requestParams.id,
			transactionId,
			false,
			requestParams.url
		);

		//Checking if token for realm is found in SecondaryTokenTable
		const secondaryTokenForRealm =
			await this.tokenManager.getCachedSecondaryTokenForRealm(
				authChallenge.getRealm(),
				requestParams.url,
				requestParams.id,
				requestParams.referenceUri
			);
		if (secondaryTokenForRealm) {
			const authResponse = await this.makeAuthorizedRequest(
				requestParams,
				secondaryTokenForRealm.token
			);
			if (authResponse.statusCode != statusConstants['401']) {
				return AuthUtils.getInstance().constructAMLResponse({
					id: requestParams.id,
					URLResponse: authResponse,
					token: requestParams.returnSecondaryToken ? secondaryTokenForRealm.token : null,
				});
			}
			//Deleting prevously used token from db as it gave 401
			await TokenManager.getInstance().deleteSecondaryTokensForRealm(
				secondaryTokenForRealm.tokenForService,
				requestParams.url,
				requestParams.referenceUri
			);
		}
		Logger.getInstance().write(
			logConstants.INFO,
			requestParams.id +
				':Getting secondary token for url ' +
				Utils.getInstance().removeQueryStringAndHashFromUrl(requestParams.url)
		);
		const result = await this.getSecondaryTokenForURL(requestParams, authChallenge);

		if (result.status == integerConstants.TOKEN_RECEIVED) {
			const resultDetails = result.details as RequestTokenResponse;
			const authResponse = await this.makeAuthorizedRequest(
				requestParams,
				resultDetails.token
			);
			response = AuthUtils.getInstance().constructAMLResponse({
				id: requestParams.id,
				URLResponse: authResponse,
				token: requestParams.returnSecondaryToken ? resultDetails.token : null,
			});
		} else {
			Logger.getInstance().write(
				logConstants.INFO,
				requestParams.id + ':Auth incomplete'
			);
			const resultDetails = result.details as RequestTokenFailureResponse;
			response = AuthUtils.getInstance().constructAMLResponse({
				id: requestParams.id,
				URLResponse: auth401Response,
				errorCode: resultDetails.errorCode,
				errorMessage: resultDetails.errorMessage,
			});
		}
		return response;
	}

	/// <summary>
	/// Fetch new secondary token from server using details from auth challenge (using makeSecondaryTokenRequest function)
	/// Take appropriate action on the response by calling processSecondaryTokenResponse
	/// </summary>
	/// <returns>SecondaryTokenResponse object (refer it's type definition for details)</returns>
	async getSecondaryTokenForURL(
		requestParams: RequestParams,
		authChallenge: AuthChallenge
	): Promise<SecondaryTokenResponse> {
		const transactionId = AuthUtils.getInstance().getCitrixTransactionIdFromHeaders(
			requestParams.headers
		);
		const secondaryTokenResponse = await this.makeSecondaryTokenRequest(
			authChallenge,
			requestParams.referenceUri,
			requestParams.id,
			transactionId,
			requestParams.url,
			requestParams.allowCredUi
		);
		return await this.processSecondaryTokenResponse(
			secondaryTokenResponse,
			requestParams,
			authChallenge,
			true
		);
	}

	/// <summary>
	/// Fetch primary token and retry fetching secondary token
	/// </summary>
	async getPrimaryAndRetryGetSecondaryToken(
		requestParams: RequestParams,
		authResponse: HTTPFetchResponse,
		authChallengeFromResponse: AuthChallenge
	): Promise<SecondaryTokenResponse> {
		const transactionId = AuthUtils.getInstance().getCitrixTransactionIdFromHeaders(
			requestParams.headers
		);
		//Got 401 response - process response to get explicit forms
		if (
			requestParams.allowCredUi ||
			(config.longLivedTokenSupport && requestParams.allowSilentLogin)
		) {
			Logger.getInstance().write(
				logConstants.INFO,
				requestParams.id +
					': calling PrimaryTokenServiceWrapper to start primary token fetch flow'
			);
			await PrimaryTokenServiceWrapper.getInstance().getPrimaryTokenForURL(
				authResponse,
				requestParams.referenceUri,
				requestParams.id,
				transactionId,
				requestParams.allowSilentLogin,
				authChallengeFromResponse.getLocations()
			);
		} else {
			//auth prompt not allowed
			Logger.getInstance().write(
				logConstants.ERROR,
				requestParams.id + ':authErrorString.DSAUTH_AUTH_PROMPT_NOT_ALLOWED'
			);
			return {
				status: integerConstants.TOKEN_NOT_RECEIVED,
				details: {
					errorCode: AMErrorCode.AM_AUTH_RESULT_INTERACTION_NOT_ALLOWED,
					errorMessage: authErrorString.DSAUTH_AUTH_PROMPT_NOT_ALLOWED,
				},
			};
		}
		const secondaryTokenResponse = await this.makeSecondaryTokenRequest(
			authChallengeFromResponse,
			requestParams.referenceUri,
			requestParams.id,
			transactionId,
			requestParams.url,
			requestParams.allowCredUi
		);
		const response = await this.processSecondaryTokenResponse(
			secondaryTokenResponse,
			requestParams,
			authChallengeFromResponse,
			false
		);
		return response;
	}

	/// <summary>
	/// parse secondary token response and store secondary token
	/// </summary>
	async parseAndStoreSecondaryToken(
		authResponse: HTTPFetchResponse,
		params: StoreSecondaryTokenFunctionParams
	): Promise<SecondaryTokenResponse> {
		const requestTokenResponse: RequestTokenResponse =
			RequestTokenResponseParser.getInstance().parseXML(
				authResponse.body
			) as RequestTokenResponse;
		Logger.getInstance().write(
			logConstants.INFO,
			params.id + ':secondary token received'
		);
		await this.tokenManager.storeSecondaryToken(params, requestTokenResponse);
		return {
			status: integerConstants.TOKEN_RECEIVED,
			details: {
				token: requestTokenResponse.token,
			},
		};
	}

	/// <summary>
	/// make a request to fetch secondary token by adding primary token in request header
	/// </summary>
	async makeSecondaryTokenRequest(
		authChallenge: AuthChallenge,
		referenceUri: string,
		id: string,
		transactionId: string,
		secondaryUrl: string,
		allowCredUi: boolean
	): Promise<HTTPFetchResponse> {
		// Check if primary is already available.
		const primaryToken = await this.getCachedPrimaryToken(
			authChallenge,
			referenceUri,
			id
		);
		Logger.getInstance().write(
			logConstants.INFO,
			id + ':Preparing secondary token request'
		);
		const authData = this.dsAuth.prepareTokenRequest(
			authChallenge,
			this.getSecondaryTokenLifetime(),
			secondaryUrl,
			transactionId
		);

		if (primaryToken) {
			if (primaryToken.serviceURL === authChallenge.getLocations()) {
				Logger.getInstance().write(
					logConstants.INFO,
					id + ':token found in PrimaryTokenTable'
				);
				authData.headers = AuthUtils.getInstance().addTokenToRequestHeader(
					authData.headers,
					primaryToken.token,
					AuthenticationMode.DSAuth
				);
			} else {
				Logger.getInstance().write(
					logConstants.INFO,
					id +
						':primarytoken serviceURL' +
						primaryToken.serviceURL +
						'and secondarytoken requestURL' +
						authChallenge.getLocations() +
						'differ'
				);
				throw new Error(stringConstants.INVALID_PRIMARY_TOKEN_SERVICE_URL);
			}
		}
		Logger.getInstance().write(
			logConstants.INFO,
			id + ':Secondary token request url' + authChallenge.getLocations()
		);
		return await authBridge.authFetch(
			stringConstants.POST,
			authChallenge.getLocations(),
			authData.headers,
			authData.body,
			referenceUri,
			allowCredUi
		);
	}

	/// <summary>
	/// process the response of a secondary token fetch request
	/// * response status code 200 = secondary token received
	/// * response status code 400/404 = auth domain change suspected
	///		this happens because when auth domain of a store has changed, it takes time to reflect
	/// 	- meanwhile 401 challenge returned by server may contain older auth domain in the realm
	/// 	if the corresponding token service URL in the 401 also contains older auth domain, this could be an invalid location hence could result in a 404 response.
	///		- if a primary token corresponding to older auth domain is used to fetch a secondary token , this gives a 400 response with "X-Citrix-Error-Reason": " invalidAuthDomain" in the response header
	///	* response status code 401 = explained with inline comments below
	/// * any other response = return secondary token fetch failure error
	/// </summary>
	async processSecondaryTokenResponse(
		authResponse: HTTPFetchResponse,
		requestParams: RequestParams,
		authChallengeFromResponse: AuthChallenge,
		showAuthPromptOn401: boolean
	): Promise<SecondaryTokenResponse> {
		let response;
		// Done with auth and store secondary token
		if (authResponse.statusCode == statusConstants['200']) {
			response = await this.parseAndStoreSecondaryToken(authResponse, {
				referenceUri: requestParams.referenceUri,
				url: requestParams.url,
				authDomain: requestParams.authDomain,
				authChallenge: authChallengeFromResponse,
				id: requestParams.id,
			});
		}
		//The same method 'processSecondaryTokenResponse' is used twice,
		//the first time when we make a secondary token request, if we get a 401, we attempt to fetch a primary token hence 'showAuthPromptOn401' flag is passed as true to start the primary token flow
		//the second time if we get a 401, this is after the user is shown auth promt once , hence 'showAuthPromptOn401' flag is passed as false so that we don't redo the primary token flow but return a failure back to native
		else if (authResponse.statusCode == statusConstants['401'] && showAuthPromptOn401) {
			response = await this.getPrimaryAndRetryGetSecondaryToken(
				requestParams,
				authResponse,
				authChallengeFromResponse
			);
		} else if (
			authResponse.statusCode == statusConstants['400'] ||
			authResponse.statusCode == statusConstants['404']
		) {
			response = {
				status: integerConstants.TOKEN_NOT_RECEIVED,
				details: {
					errorCode: AMLErrorCode.AML_POSSIBLE_AUTH_DOMAIN_CHANGE,
					errorMessage: authErrorString.DSAUTH_TOKEN_REQUEST_RESPONSE_STATUS_400_OR_404,
				},
			};
		} else {
			response = {
				status: integerConstants.TOKEN_NOT_RECEIVED,
				details: {
					errorCode: AMLErrorCode.AML_SECONDARY_TOKEN_FETCH_FAIL,
					errorMessage: authErrorString.DSAUTH_TOKEN_REQUEST_RESPONSE_STATUS_UNCHECKED,
				},
			};
		}
		return response;
	}

	protected abstract getCachedPrimaryToken(
		authChallenge: AuthChallenge,
		referenceUri: string,
		id: string
	): Promise<PrimaryTokenTableEntry>;

	protected abstract getSecondaryTokenLifetime(): string;
}
